import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { Cursor, Sort } from './util.js';
import { Status } from '../util/schemas.js';
const ListInput = z.object({
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }).optional(),
    limit: z.number().optional(),
});
const SectionsFilters = ListInput.extend({
    filters: z
        .object({
        locationId: z.string().optional(),
        wardId: z.string().or(z.array(z.string())).optional(),
        name: z.string().optional(),
        status: z.array(Status).optional(),
    })
        .optional(),
});
const WardsFilters = ListInput.extend({
    filters: z
        .object({
        locationId: z.string().optional(),
        sectionId: z.string().or(z.array(z.string())).optional(),
        name: z.string().optional(),
        status: z.array(Status).optional(),
    })
        .optional(),
});
export const locationsQueries = {
    listLocations: defineQuery(_ctx => {
        return zql.locations.where('id', 'IS NOT', null);
    }),
    listSections: defineQuery(SectionsFilters, ({ args }) => {
        let base = zql.sections.where('deletedAt', 'IS', null);
        const filters = args.filters;
        if (filters) {
            if (filters.name?.trim())
                base = base.where(({ and, cmp }) => and(cmp('name', 'ILIKE', `%${filters.name?.trim()}%`)));
            if (filters.locationId) {
                base = base.where('locationId', '=', filters.locationId);
            }
            if (filters.status?.length) {
                base = base.where('status', 'IN', filters.status);
            }
            if (filters.wardId?.length) {
                if (Array.isArray(filters.wardId)) {
                    base = base.whereExists('wards', q => q.where('id', 'IN', filters.wardId));
                }
                else if (typeof filters.wardId === 'string') {
                    base = base.whereExists('wards', q => q.where('id', filters.wardId));
                }
            }
        }
        base = applyCursor(base, { sort: args.sort, cursor: args.cursor, limit: args.limit });
        return base;
    }),
    listWards: defineQuery(WardsFilters, ({ args }) => {
        let base = zql.wards.related('section').related('created').where('deletedAt', 'IS', null);
        const filters = args.filters;
        if (filters) {
            if (filters.name?.trim()?.length) {
                base = base.where(({ and, cmp }) => and(cmp('name', 'ILIKE', `%${filters.name?.trim()}%`)));
            }
            if (filters.locationId) {
                base = base.where('locationId', '=', filters.locationId);
            }
            if (filters.status?.length) {
                base = base.where('status', 'IN', filters.status);
            }
            if (filters.sectionId?.length) {
                if (Array.isArray(filters.sectionId)) {
                    base = base.where('sectionId', 'IN', filters.sectionId);
                }
                else if (typeof filters?.sectionId === 'string') {
                    base = base.where('sectionId', '=', filters.sectionId);
                }
            }
        }
        base = applyCursor(base, { sort: args.sort, cursor: args.cursor, limit: args.limit });
        return base;
    }),
    getSectionById: defineQuery(z.string(), ({ args }) => {
        return zql.sections
            .related('location')
            .related('address')
            .related('created')
            .related('wards', q => q.where('deletedAt', 'IS', null))
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getSectionActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', 'section')
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    getSectionUsersById: defineQuery(z.string(), ({ args }) => {
        return zql.userAccessLocation.related('user').where('sectionId', args);
    }),
    getWardById: defineQuery(z.string(), ({ args }) => {
        return zql.wards
            .related('section')
            .related('location')
            .related('created')
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getWardBySectionId: defineQuery(z.string(), ({ args }) => {
        return zql.wards.where('sectionId', '=', args);
    }),
    getWardActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', 'ward')
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    getWardUsersById: defineQuery(z.string(), ({ args }) => {
        return zql.userAccessLocation.related('user').where('wardId', args);
    }),
    getSectionsWithPatientEncounters: defineQuery(() => {
        return zql.sections
            .where('deletedAt', 'IS', null)
            .whereExists('patientsEncounters', encounters => encounters.where('deletedAt', 'IS', null));
    }),
    getWardsWithPatientEncounters: defineQuery(() => {
        return zql.wards
            .where('deletedAt', 'IS', null)
            .whereExists('patientsEncounters', encounters => encounters.where('deletedAt', 'IS', null));
    }),
};
//# sourceMappingURL=location.queries.js.map